import { useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, onSnapshot, collection, getDocs } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from './config/firebaseConfig.js';
import { useUserProfile } from './userProfileContext';

const getHighResPhotoURL = (photoURL) => {
  if (!photoURL) return null;
  return photoURL.replace('s96-c', 's400-c');
};

export function useFirebaseProfile() {
  const { 
    userProfile, 
    updateUserProfile, 
    updateProfileField, 
    clearUserProfile,
    loading,
    setLoadingState,
    error,
    setErrorState
  } = useUserProfile();

  const [companyOptions, setCompanyOptions] = useState([]);
  const [interestOptions, setInterestOptions] = useState([]);
  const [seasCourseOptions, setSeasCourseOptions] = useState([]);

  const fetchSEASCourses = useCallback(async () => {
    try {
      const coursesCollection = collection(db, 'seascourses');
      const coursesSnapshot = await getDocs(coursesCollection);
      
      const coursesList = coursesSnapshot.docs.map(doc => ({ value: doc.id, label: doc.id }));
      setSeasCourseOptions(coursesList);
      
      console.log(`Fetched ${coursesList.length} SEAS courses`);
    } catch (err) {
      console.error("Error fetching SEAS courses:", err);
      setSeasCourseOptions([]);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const companiesCollection = collection(db, 'companies');
      const companiesSnapshot = await getDocs(companiesCollection);
      
      const companiesList = companiesSnapshot.docs.map(doc => ({ value: doc.id, label: doc.id }));
      setCompanyOptions(companiesList);
      
      console.log(`Fetched ${companiesList.length} companies`);
    } catch (err) {
      console.log("Error fetching companies, possibly because the collection doesn't exist yet:", err);
      setCompanyOptions([]);
    }
  }, []);

  const fetchInterests = useCallback(async () => {
    try {
      const interestCollection = collection(db, 'interests');
      const interestSnapshot = await getDocs(interestCollection);
      // permissions issues
      const interestList = interestSnapshot.docs.map(doc => ({ value: doc.id, label: doc.id }));
      setInterestOptions(interestList); // Ensure interestOptions state is updated
  
      console.log(`Fetched ${interestList.length} interests`);
    } catch (err) {
      console.log("Error fetching interests, possibly because the collection doesn't exist yet:", err);
      setInterestOptions([]); // Set an empty array if fetch fails
    }
  }, []);
  

  useEffect(() => {
    console.log("useFirebaseProfile effect starting");
    let unsubscribeSnapshot = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setLoadingState(true);
      setErrorState(null);

      if (user) {
        console.log(`Fetching profile for user: ${user.uid}`);
        const userDocRef = doc(db, "users", user.uid);

        try {
          const docSnap = await getDoc(userDocRef);
          let profileData;
          const highResPhotoURL = getHighResPhotoURL(user.photoURL);

          if (docSnap.exists()) {
            profileData = docSnap.data();
            console.log("Profile data received", profileData);
            
            if (highResPhotoURL && !profileData.hasOwnProperty('photoURL')) {
              await setDoc(userDocRef, { photoURL: highResPhotoURL }, { merge: true });
              profileData.photoURL = highResPhotoURL;
            }
          } else {
            console.log("No profile data available, creating new profile");
            profileData = { 
              uid: user.uid, 
              email: user.email,
              photoURL: highResPhotoURL,
              displayName: user.displayName || '',
            };
            await setDoc(userDocRef, profileData);
          }

          updateUserProfile(profileData);
          await fetchCompanies();
          await fetchInterests();
          await fetchSEASCourses();
        } catch (err) {
          console.error("Error fetching/creating profile", err);
          setErrorState(err.message);
        } finally {
          setLoadingState(false);
        }

        unsubscribeSnapshot = onSnapshot(userDocRef, (doc) => {
          if (doc.exists()) {
            const profileData = doc.data();
            console.log("Profile data updated", profileData);
            updateUserProfile(profileData);
          } else {
            console.log("No profile data available");
            clearUserProfile();
          }
        }, (err) => {
          console.error("Error in real-time profile update", err);
          setErrorState(err.message);
        });
      } else {
        console.log("No user, clearing profile");
        clearUserProfile();
        setLoadingState(false);
        if (unsubscribeSnapshot) {
          unsubscribeSnapshot();
          unsubscribeSnapshot = null;
        }
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
      }
      console.log("useFirebaseProfile effect cleanup");
    };
  }, [updateUserProfile, clearUserProfile, setLoadingState, setErrorState, fetchCompanies, fetchInterests, fetchSEASCourses]);

  const addCompany = async (company) => {
    try {
      await setDoc(doc(db, 'companies', company), { name: company }, { merge: true });
      setCompanyOptions(prevOptions => {
        if (!prevOptions.some(option => option.value === company)) {
          return [...prevOptions, { value: company, label: company }];
        }
        return prevOptions;
      });
      console.log(`Added company: ${company}`);
    } catch (err) {
      console.error("Error adding company", err);
      setErrorState(err.message);
    }
  };

  const addInterest = async (interest) => {
    try {
      await setDoc(doc(db, 'interests', interest), { name: interest }, { merge: true });
      setInterestOptions(prevOptions => {
        if (!prevOptions.some(option => option.value === interest)) {
          return [...prevOptions, { value: interest, label: interest }];
        }
        return prevOptions;
      });
      console.log(`Added interest: ${interest}`);
    } catch (err) {
      console.error("Error adding interest", err);
      setErrorState(err.message);
    }
  };

  const removeUndefinedFields = (obj) => {
    return Object.keys(obj).reduce((acc, key) => {
      if (obj[key] !== undefined) {
        acc[key] = obj[key];
      }
      return acc;
    }, {});
  };

  const updateProfileFieldFirebase = async (field, value) => {
    if (!auth.currentUser) {
      console.error("No authenticated user");
      throw new Error("No authenticated user");
    }
    const userDocRef = doc(db, "users", auth.currentUser.uid);
    try {
      console.log(`Updating profile field: ${field}`);
      if (value !== undefined) {
        await updateDoc(userDocRef, { [field]: value });
        updateProfileField(field, value);
        console.log("Profile field updated successfully");
      } else {
        console.log(`Skipping update for field ${field} due to undefined value`);
      }
    } catch (err) {
      console.error("Error updating profile field", err);
      setErrorState(err.message);
      throw err;
    }
  };

  const updateProfileBatch = async (fields) => {
    if (!auth.currentUser) {
      console.error("No authenticated user");
      throw new Error("No authenticated user");
    }
    const userDocRef = doc(db, "users", auth.currentUser.uid);
    try {
      const filteredFields = removeUndefinedFields(fields);
      console.log(`Updating profile fields: ${Object.keys(filteredFields).join(', ')}`);
      if (Object.keys(filteredFields).length > 0) {
        await updateDoc(userDocRef, filteredFields);
        updateUserProfile(filteredFields);
        console.log("Profile fields updated successfully");
      } else {
        console.log("No fields to update after filtering out undefined values");
      }
    } catch (err) {
      console.error("Error updating profile fields", err);
      setErrorState(err.message);
      throw err;
    }
  };

  const uploadPhotoToFirebaseStorage = async (file) => {
    if (!auth.currentUser) {
      throw new Error("No authenticated user found");
    }
    const userId = auth.currentUser.uid;
    const storageRef = ref(storage, `profile-pictures/${userId}/${file.name}`);

    const metadata = {
      contentType: file.type
    };

    try {
      const snapshot = await uploadBytes(storageRef, file, metadata);
      console.log('Uploaded a blob or file!', snapshot);
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('File available at', downloadURL);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.code === 'storage/unauthorized') {
        console.error('User doesn\'t have permission to access the object');
      }
      throw error;
    }
  };

  console.log("useFirebaseProfile render", { loading, userProfile, error, companyOptions, interestOptions });
  return { 
    profile: userProfile, 
    loading, 
    error, 
    updateProfileField: updateProfileFieldFirebase, 
    updateProfileBatch,
    uploadPhotoToFirebaseStorage,
    companyOptions, 
    interestOptions,
    addCompany,
    addInterest,
    fetchCompanies,
    fetchInterests,
    seasCourseOptions,
  };
}